/**
 * frontend/src/stores/overviewBrandStore.ts
 *
 * #340 D-CHANTIER3-REFONDRE-P2 (22/07) — store LOCAL à l'Overview (page
 * `/phenomena/marche` + son widget sidebar MARKET OVERVIEW). Marque focus pour
 * l'aiguillage vue persistée vs live.
 *
 * PORTÉE STRICTEMENT LOCALE — n'écrit dans AUCUN autre state :
 *   - PAS useMxRmFlowScope (module Marché /marche)
 *   - PAS filtersByModule (preferencesStore)
 *   - PAS useMarcheFilters (canal, période, brandGrouped)
 *
 * Le module /marche reste totalement indépendant : sélectionner Realme dans
 * l'Overview ne modifie NI le tableau S1MarketShareTable, NI le treemap S5,
 * NI le brand360 focus.
 *
 * Sémantique :
 *   brandCode : null      → "Marché total" (chemin PERSISTÉ, Samsung POV, cartes suivies)
 *   brandCode : "REALME"  → marque tierce (chemin LIVE, non persisté, badge "vue vivante")
 *
 * Convention : le code Samsung n'est PAS stocké (Samsung ≡ "Marché total" car
 * les phénomènes MARCHE en base sont Samsung POV). Le sélecteur affiche donc
 * "Marché total (Samsung ★)" comme entrée unique + les marques tierces.
 */

import { create } from 'zustand';

interface OverviewBrandStore {
  /** null = Marché total (défaut). Sinon = brand.code (ex "REALME"). */
  brandCode: string | null;
  /** Défini le focus marque. Passer null pour revenir à "Marché total". */
  setBrandCode: (code: string | null) => void;
  /**
   * #340 P2-ADD (22/07) — Mode "Tous canaux" LOCAL à l'Overview.
   * false (défaut) → suit le store partagé `useMarcheFilters().channelType`
   *                  (retail | wholesale). Cohérent avec /marche.
   * true           → aucun filtre canal envoyé à `usePhenomenaActive` — le
   *                  chemin persisté renvoie retail + wholesale + all mêlés.
   *                  Les cartes doivent alors porter un badge canal (l'UI le
   *                  gère via prop `showChannelBadge` sur PhenomenaAlertsView).
   * Ne modifie PAS le store partagé — /marche conserve sa valeur retail/wholesale.
   * Ignoré si `brandCode` défini (marque tierce = live, endpoint refuse 'all').
   */
  allChannels: boolean;
  setAllChannels: (v: boolean) => void;
}

export const useOverviewBrandStore = create<OverviewBrandStore>((set) => ({
  brandCode: null,
  setBrandCode: (code) => set({ brandCode: code }),
  // #340 P2-ADD (22/07) — Défaut "All" (Tous canaux) demandé Hkm : panorama
  // complet à l'ouverture, l'utilisateur restreint ensuite à un canal s'il veut.
  allChannels: true,
  setAllChannels: (v) => set({ allChannels: v }),
}));

// ── Sélecteurs mémoïsés ──────────────────────────────────────────────────────

/** brandCode courant (re-render quand il change). */
export const useOverviewBrandCode = () => useOverviewBrandStore((s) => s.brandCode);
/** Action setter (stable). */
export const useSetOverviewBrand   = () => useOverviewBrandStore((s) => s.setBrandCode);
/** Mode "Tous canaux" local (re-render quand il change). */
export const useOverviewAllChannels    = () => useOverviewBrandStore((s) => s.allChannels);
/** Action setter (stable). */
export const useSetOverviewAllChannels = () => useOverviewBrandStore((s) => s.setAllChannels);
